<template>
  <div class="page-container flex-container">
    <ModalBox :visible="showModal" :infoTitle="infoTitle" :infoMessage="infoMessage" @close="showModal = false" />
    <!-- <CustomAnimatedLogo></CustomAnimatedLogo> -->
    
    <!-- <CustomAnimatedLogo v-if="isInProgress"></CustomAnimatedLogo> -->
    <div class="container">
      <div v-if ="!showResults">
        <br>
        <h5 class="flex-container">Optimal Battery Size Estimator</h5>

        <form @submit.prevent="submitForm">

        <div class="form-grid">
          
          <!-- Row 1 -->
            <div>
                <label for="usageType">Type of Usage</label>

                <select
                    v-model="form.use_type"
                    :class="{ 'input-error': errors.use_type }"
                >
                    <option disabled value="">Select Usage Type</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                </select>

                <p v-if="errors.use_type" class="error-text">
                    {{ errors.use_type }}
                </p>
            </div>

            <div> 
              <label for="solarSize">Solar System Size (kW)</label>
              <input type="number" min="0" v-model="form.solar_size" :class="{ 'input-error': errors.solar_size }"/>
              <p v-if="errors.solar_size" class="error-text">
                {{ errors.solar_size }}
              </p>
            </div>

            <div> 
              <label for="inverterSize">Inverter Size (kW)</label>
              <input type="number" min="0" v-model="form.inv_size" :class="{ 'input-error': errors.inv_size }"/>
              <p v-if="errors.inv_size" class="error-text">
                {{ errors.solar_size }}
              </p>
            </div>

            <div> 
              <label for="avgDaily">Average Daily Consumption (kWh)</label>
              <input type="number" min="0" v-model="form.avg_daily_consumption" :class="{ 'input-error': errors.avg_daily_consumption }"/>
              <p v-if="errors.avg_daily_consumption" class="error-text">
                {{ errors.avg_daily_consumption }}
              </p>
            </div>

            <!-- Submit -->
            <div class="submit-row">
              <button class="submit-button" type="submit" :disabled="isInProgress">
                Submit
              </button>
            </div>

        </div>
        </form>      
      </div>
    
      <div v-else class="recepit-container">
        <h4 class="receipt-title"> Battery Sizing Report </h4>

        <div class="receipt-card">
          <div class="receipt-row">
            <span> Type of Usage </span>
            <strong> {{ resultsData.usageType }} </strong>
          </div>

          <div class="receipt-row">
            <span> Solar System Size </span>
            <strong> {{ resultsData.solarSize }} kW </strong>
          </div>

          <div class="receipt-row">
            <span> Inverter Size </span>
            <strong> {{ resultsData.inv_size }} kW </strong>
          </div>

          <div class="receipt-row">
            <span> Estimated Daily PV Generation </span>
            <strong> {{ resultsData.estimatedPV }} kWh/day </strong>
          </div>

          <div class="receipt-row">
            <span> Average Daily Consumption </span>
            <strong> {{ resultsData.avgConsumption }} kWh </strong>
          </div>

          <!-- <div class="receipt-row">
            <span> Estimated Peak Load </span>
            <strong> {{ resultsData.peakLoad }} kWh </strong>
          </div> -->

          <!-- <div class="receipt-row">
            <span> Estimated Non-Peak Load </span>
            <strong> {{ resultsData.nonPeakLoad }} kWh </strong>
          </div> -->

          <div class="receipt-row highlight">
            <span> Optimal Battery Size </span>
            <strong> {{ resultsData.batteryRange }} </strong>
          </div>

          <div v-if="resultsData.comments" class="comments-box">
            {{ resultsData.comments }}
          </div>
        </div>

        <button class="submit-button" @click="goBack">
          Back
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, onMounted, onUnmounted, watch, computed } from 'vue';
import ModalBox from '@/components/cards/ModalBox.vue';
import CustomAnimatedLogo from '../cards/CustomAnimatedLogo.vue';
import StyledInfoBox from '../cards/StyledInfoBox.vue';
import VueApexCharts from 'vue3-apexcharts';
import StyledInfoWithIcon from '../cards/StyledInfoWithIcon.vue';
import { getDevModeState } from '@/services/cookieService';
import apiClient from '@/services/apiService';
import axios from 'axios';
import { utcYears } from 'd3-time';
import { re } from 'mathjs';
// import { setTimeout } from 'core-js';

// Function to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data URL prefix
    reader.onerror = (error) => reject(error);
  });
};

export default {
  name: 'SimulatorPage',
  components: {
    ModalBox,
    CustomAnimatedLogo,
    StyledInfoBox,
    apexChart: VueApexCharts,
    StyledInfoWithIcon,
  },
  computed: {

  },
  setup() {
    const tableData = ref([]);

    const errors = ref({
        use_type: '',
        solar_size: '',
        inv_size: '',
        avg_daily_consumption: '',
        load_peak: '',
        load_non_peak: '',
        peak_demand: ''
    });

    const hasSubmitted = ref(false);
    const showResults = ref(false)

    const resultsData = ref({
      usageType: '',
      solarSize: '',
      inv_Size: '',
      estimatedPV: '',
      avgConsumption: '',
      peakLoad: '',
      nonPeakLoad: '',
      batteryRange: '',
      comments: ''
    })

    // validate form
    const validateForm = () => {
      let isValid = true;

      // Reset errors
      errors.value = {
        use_type: '',
        solar_size: '',
        inv_size: '',
        avg_daily_consumption: '',
        load_peak: '',
        load_non_peak: '',
        peak_demand: ''
      };

      if (!form.value.use_type){
        errors.value.use_type = 'Usage type is required';
        isValid = false;
      }      

      if (!form.value.solar_size || form.value.solar_size <= 0) {
        errors.value.solar_size = 'Value is required';
        isValid = false;
      }

      if (!form.value.inv_size || form.value.inv_size <= 0) {
        errors.value.inv_size = 'Value is required';
        isValid = false;
      }

      if (!form.value.avg_daily_consumption || form.value.avg_daily_consumption <= 0) {
        errors.value.avg_daily_consumption = 'Value is required';
        isValid = false;
      }

      return isValid;
    };

    const form = ref({
      use_type: '',
      solar_size: 0,
      inv_size: 0,
      avg_daily_consumption: 0,
      load_peak: 0,
      load_non_peak: 0,
      peak_demand: 0
    });

    const showModal = ref(false);
    const infoTitle = ref('');
    const infoMessage = ref('');

    const isDarkMode = ref(document.documentElement.classList.contains('dark-mode'));

    const updateTheme = () => {
      isDarkMode.value = document.documentElement.classList.contains('dark-mode');
    };

    const submitForm = () => {
      if (!validateForm()) return;

      const solar_size = Number(form.value.solar_size)
      const inv_size = Number(form.value.inv_size);
      const avgConsumption = Number(form.value.avg_daily_consumption);

      const peakSunHours = 5.0;
      const efficiency = 0.80;

      let estimatedPV = solar_size * peakSunHours * efficiency;
      const dcacRatio = solar_size / inv_size

      let clippingFactor = 1.0;

      if (dcacRatio > 1.35) {
        clippingFactor = 0.93;
      }

      else if (dcacRatio > 1.2) {
        clippingFactor = 0.97;
      }

      estimatedPV = estimatedPV * clippingFactor

      const peakLoad = avgConsumption * 0.37;
      const nonPeakLoad = avgConsumption * 0.63;

      const pvSurplus =  estimatedPV - peakLoad;

      const minBattery = (nonPeakLoad / 0.9).toFixed(1);
      const maxBattery = (nonPeakLoad / 0.8).toFixed(1);

      let comments = '';

      if (pvSurplus < nonPeakLoad){
        comments = 'A larger PV system is recommended to better support non-peak energy consumption and battery charging'
      }

      resultsData.value = {
        usageType: form.value.use_type,
        solarSize: solar_size.toFixed(1),
        inv_size: inv_size.toFixed(1),
        estimatedPV: estimatedPV.toFixed(1),
        avgConsumption: avgConsumption.toFixed(1),
        peakLoad: peakLoad.toFixed(1),
        nonPeakLoad: nonPeakLoad.toFixed(1),
        batteryRange: `${minBattery} - ${maxBattery} kWh`,
        comments
      };

      showResults.value = true;
      
    };

    const goBack = () => {
      showResults.value = false;
    };

    onMounted(() => {
      updateTheme();

      const observer = new MutationObserver(updateTheme);
      observer.observe(document.documentElement, {attributes: true, attributeFilter: ['class']});
    });

    // watch(
    //   () => ({ ...form.value}),
    //   () => {
    //     tableData.value = [];
    //     hasSubmitted.value = false;
    //   },
    //   { deep: true }
    // );

    watch(
      () => ({
        use_type: form.value.use_type,
        solar_size: form.value.solar_size,
        inv_size: form.value.inv_size,
        avg_daily_consumption: form.value.avg_daily_consumption,
        load_peak: form.value.load_peak,
        load_non_peak: form.value.load_non_peak
      }),
      () => {

        // Reset calculated battery recommendation
        form.value.peak_demand = '';

        // Reset form state
        hasSubmitted.value = false;

        // Optional: clear modal
        showModal.value = false;

      },
      { deep: true }
    );

  //  console.log('Debug flag 6');
    return {
      form,
      tableData,
      showModal,
      infoTitle,
      infoMessage,
      hasSubmitted,
      errors,
      validateForm,
      isDarkMode,
      submitForm,
      showResults,
      resultsData,
      goBack,
    };
  },
};
</script>

<style scoped>
.container {
  /* padding: 20px;
  max-width: 70vw;
  margin: auto; */
  padding: 20px;
  width: 1000px;
  max-width: 1000px;
  margin: auto;
}

.form-grid {
  display: grid;
  grid-template-columns: 2fr 1;
  gap: 20px;
  width: 100%;
}

.submit-row {
  grid-column: 1 / 3;
}

.submit-row button {
  width: 100%;
  height: 45px;
  font-weight: 600;
}

.form-grid input,
.form-grid .select-display {
  height: 40px;
  box-sizing: border-box;
}

.input-section,
.output-section,
.upload-section {
  background: #f4f4f4;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
}

.error-text {
  color: #d32f2f;
  font-size: 0.7rem;
  margin-top: -6px;
  margin-bottom: 6px;
}

.input-error {
  border: 1px solid #d32f2f !important;
}

input,
select,
.submit-button {
  width: 100%;
  padding: 10px;
  margin: 10px 0;
  border-radius: 8px;
  border-width: 0.1px;
}

input,
select,
button:focus {
  border-width: 0.06px;
  border-color: green;
}

input,
select {
  background-color: var(--card-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

button {
  background-color: #4CAF50;
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 8px;
}

.receipt-container {
  max-width: 700px;
  margin: auto;
}

.receipt-title {
  text-align: center;
  margin-bottom: 20px;
}

.receipt-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 24px;
  border: 1px solid var(--border-color);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.receipt-row {
  display: flex;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid var(--border-color);
}

.receipt-row.highlight {
  margin-top: 20px;
  padding: 22px;
  border-radius: 14px;

  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.15),
    rgba(46, 125, 50, 0.08)
  );

  border: 2px solid #4CAF50;

  display: flex;
  justify-content: space-between;
  align-items: center;

  box-shadow: 0 6px 18px rgba(76, 175, 80, 0.18);
}

.receipt-row.highlight span {
  font-size: 1rem;
  font-weight: 700;
  color: #2e7d32;
}

.receipt-row.highlight strong {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1b5e20;
}

.comments-box {
  margin-top: 20px;
  padding: 14px;
  border-radius: 8px;
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid #ffc107;
  color: var(--text-primary);
}

button:hover {
  background-color: #45a049;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.twin-fields {
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
}

.twin-fields div {
  width: 48%; /* Adjust width as needed */
}

.important-context {
  font-weight: bold;
}

.select-wrapper {
  position: relative;
  width: 100%;
}

.select-display {
  width: 100%;
  padding: 10px;
  margin: 10px 0;

  border-radius: 8px;
  border: 0.1px solid var(--border-color);
  background: var(--card-bg);
  color: var(--text-primary);

  font-size: 70%;
  cursor: pointer;

  display: flex;
  align-items: center;
}

.select-display:focus,
.select-display:focus-visible
.select-display:focus-within{
  outline: none;
  border-width: #2684ff;
  border-color: green;
  box-shadow: 0 0 0 1px #2684ff;
}

.select-display:hover {
  border-color: #4CAF50;
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;

  background: var(--card-bg);
  color: var(--text-primary);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  list-style: none;
  margin: 0;
  padding: 6px 0;

  max-height: 108px; /* ≈ 3 items */
  overflow-y: auto;

  z-index: 1000;
}

.select-dropdown li {
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.75rem;
}

.select-dropdown li:hover {
  background-color: #f1f7f4;
  color: #2e7d32;
}

.data-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 12px 10px rgba(0, 0 0, 0.1);
  margin-top: 30px;
}

.table-header-1 {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.table-header-2 {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.date-col {
  width: 120px;
}

.time-col {
  width: 100px;
}

.value-col {
  width: 180px;
}

.no-data-message {
  padding: 24px;
  text-align: center;
  /* color: #666; */
  font-size: 0.85rem;
  font-weight: 500;
  /* background: #fafafa; */
  background-color: var(--input-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.inline-spinner {
  margin-top: 6px;
  width: 16px;
  height: 16px;
  border: 2px solid #ccc;
  border-top-color: #555;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-wrapper {
  display: flex;
  justify-content: center;
  /* margin-top: 8px; */
  height: 24px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}


th, td {
    border: 1px solid #ccc;
    padding: 8px;
    text-align: center;
    font-size: 0.8rem;
  }

  h1, h2, h4, h5, h6, p, label, input, select, button, th, td {
      font-size: 85%; /* Set font size to % of the original size */
  }

  .savings-info{
    display: flex;
    justify-content: space-evenly;
  }

  .custom-table > th, td {
    border: none; /* Makes borders invisible */
    font-size: 1rem; /* Set font size to 2rem */;
  }

  .output-header {
    background-color: white;
    border-radius: 1px;
  }

  table.custom-table thead th {
    background-color: white; /* Use more specific selector */
    color: black; /* Ensure text is visible */
    border-radius: 8px;
    font-size: 1rem;
  }

  table.custom-table {
    border-collapse: separate; /* Change to 'separate' for border radius to work */
    border-spacing: 0; /* Remove default spacing between cells */
    border-radius: 10px; /* Adjust as needed for desired roundness */
    overflow: hidden; /* Prevent content from overflowing */
  }

  #preview-image {
    max-width: 80%;
    height: auto;
    margin: 10px;
  }


  @media (max-width: 750px) {

    th, td {
    border: 1px solid #ccc;
    padding: 3px;
    text-align: center;
    font-size: 0.3rem;
    }

    .savings-info{
      display: flex;
      flex-direction: column;
      justify-content: center; /* Centers items vertically */
      align-items: center; /* Centers items horizontally */
    }

    table.custom-table thead th {
    background-color: white; /* Use more specific selector */
    color: black; /* Ensure text is visible */
    border-radius: 8px;
    font-size: 0.4rem;
    }
}

* {
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}
</style>
